import { motion } from "framer-motion";
import { ArrowRight, Instagram, Leaf, Linkedin } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";

const TYPEFORM_URL =
  "https://form.typeform.com/to/Gx3oVrwN?typeform-source=shyrabhalla.com";

const programHighlights = [
  "Nutrition plan fully personalized to your current health status, goals, and daily routines",
  "Supplement strategy tailored to your goals",
  "Movement guidance and wellness habit-building",
  "A holistic strategy addressing fatigue, bloating, cravings, skin, and hormonal balance",
];

const testimonials = [
  {
    text: "Working with Shyra changed everything for me. My bloating disappeared within weeks, my skin cleared up, and I finally feel confident in my own body again. Everything felt so personal and gentle, and she was there to support me with all my requests and questions, every step of the way.",
    author: "S",
  },
  {
    text: "I’ve tried every diet, but nothing felt sustainable until this. Shyra understood my lifestyle, my cravings, my stresses, and built a plan around me. I lost weight without feeling restricted, but more importantly, I fixed issues I used to think I’d have to live with forever. So grateful for this.",
    author: "A",
  },
  {
    text: "I came to Shyra as a bride-to-be with the same goal as anyone else in my position has: to look my best on my wedding day. Within a month, my energy was like an 8 year old’s, I lost a couple inches off my waist and arm, my skin was glowing (even with all the makeup I had been using), and somehow, my period cramps disappeared. I felt better in every way. Bride-to-be or not, this is the best investment I’ve made in myself.",
    author: "A",
  },
  {
    text: "I had been trying for years... This one was the only plan that actually gave me real results. 6 months later and I’m still practicing the habits I formed. I’ve never felt so in control.",
    author: "M",
  },
  {
    text: "I feel like a human again. 6 kilos down, no more bloating, and still going strong.",
    author: "K",
  },
];

const sectionVariants = {
  hidden: { opacity: 0, y: 26 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.04,
    },
  },
};

export default function HomePage() {
  const { hash } = useLocation();
  const isTestimonialsHash = hash === "#testimonials";

  return (
    <div className="relative overflow-x-hidden bg-reset-cream pt-20 text-reset-mocha" data-testid="home-page-root">
      <div className="grain-overlay" aria-hidden="true" />
      <Navbar />

      <header className="relative isolate min-h-[90vh]" data-testid="home-hero-section">
        <img
          src="https://images.unsplash.com/photo-1617105246082-6392e241dfda?auto=format&fit=crop&w=1920&q=70"
          alt="Minimal wellness background"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
          data-testid="home-hero-background-image"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#FDFBF7D9] via-[#FDFBF7EE] to-[#F5F0E6]" />

        <motion.div
          className="mx-auto flex min-h-[90vh] w-full max-w-6xl flex-col justify-center px-6 py-20 md:px-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="mb-7 w-fit rounded-2xl border border-[#D4C5B0] bg-[#FDFBF7]/80 px-4 py-3"
            data-testid="home-hero-name-card"
          >
            <p className="font-clean text-xl font-bold tracking-[0.04em] text-[#3F322A]" data-testid="home-hero-name-text">
              SHYRA BHALLA
            </p>
            <p className="mt-1 text-sm text-[#6B5A4E]" data-testid="home-hero-credentials-text">
              Nutritionist, health coach, weight management specialist
            </p>
          </div>

          <h1
            className="max-w-4xl font-heading text-4xl leading-[1.1] text-[#4A3B32] sm:text-5xl lg:text-6xl"
            data-testid="home-hero-title"
          >
            A better way to look, feel &amp; live
          </h1>
          <p
            className="mt-7 max-w-3xl text-base leading-relaxed text-[#5B4A3F] md:text-lg"
            data-testid="home-hero-subtitle"
          >
            Personalized nutrition, wellness &amp; body transformation. Rooted
            in science, designed for your lifestyle.
          </p>

        </motion.div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-col gap-16 px-6 py-16 md:gap-24 md:px-12 md:py-24">
        <div
          className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-start"
          data-testid="home-photo-start-here-layout"
        >
          <motion.section
            className="mx-auto w-full max-w-md lg:order-2 lg:col-span-4 lg:max-w-none"
            variants={sectionVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            data-testid="home-feature-photo-section"
          >
            <div
              className="overflow-hidden rounded-[2rem] border border-[#D8CCBD] bg-[#EDE4D8] shadow-[0_12px_30px_rgba(74,59,50,0.08)]"
              data-testid="home-feature-photo-wrap"
            >
              <img
                src="/images/home-shyra-photo.webp"
                alt="Shyra enjoying a green juice in an active wellness setting"
                className="h-auto w-full object-contain"
                data-testid="home-feature-photo-image"
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.section>

          <motion.section
            className="rounded-[2rem] border border-[#E6D9CB] bg-white/65 p-8 md:p-10 lg:order-1 lg:col-span-8"
            variants={sectionVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            data-testid="home-start-here-section"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A6658]" data-testid="home-start-here-eyebrow">
              Start here
            </p>
            <h2 className="mt-3 font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl" data-testid="home-start-here-title">
              A simple first step toward lasting results.
            </h2>

            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="home-start-here-steps-grid">
              <article className="rounded-2xl border border-[#E0D6C8] bg-[#FAF5EC] p-5" data-testid="home-start-step-1">
                <p className="text-xs uppercase tracking-[0.15em] text-[#7A6658]" data-testid="home-start-step-1-label">
                  Step 1
                </p>
                <p className="mt-2 text-base text-[#4A3B32]" data-testid="home-start-step-1-text">
                  Explore The Program and see if it aligns with your goals.
                </p>
              </article>

              <article className="rounded-2xl border border-[#E0D6C8] bg-[#F6EEE2] p-5" data-testid="home-start-step-2">
                <p className="text-xs uppercase tracking-[0.15em] text-[#7A6658]" data-testid="home-start-step-2-label">
                  Step 2
                </p>
                <p className="mt-2 text-base text-[#4A3B32]" data-testid="home-start-step-2-text">
                  Understand the full framework and personalized support included.
                </p>
              </article>

              <article className="rounded-2xl border border-[#E0D6C8] bg-[#F1E7D9] p-5" data-testid="home-start-step-3">
                <p className="text-xs uppercase tracking-[0.15em] text-[#7A6658]" data-testid="home-start-step-3-label">
                  Step 3
                </p>
                <p className="mt-2 text-base text-[#4A3B32]" data-testid="home-start-step-3-text">
                  Begin your reset journey with a plan built around your life.
                </p>
              </article>
            </div>

            <div className="mt-8" data-testid="home-start-here-cta-wrap">
              <Button
                asChild
                className="h-auto rounded-full bg-[#4A3B32] px-8 py-4 text-base text-[#FDFBF7] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#6B5A4E]"
              >
                <Link to="/the-program" data-testid="home-start-here-program-button">
                  Go to The Program <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </motion.section>
        </div>

        <motion.section
          className="grid grid-cols-1 items-start gap-10 md:grid-cols-12 md:gap-14"
          variants={sectionVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          data-testid="home-personalized-nutrition-section"
        >
          <div className="md:col-span-7">
            <h2
              className="font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl"
              data-testid="home-personalized-nutrition-title"
            >
              Personalised nutrition, real results
            </h2>

            <p className="mt-7 text-base leading-relaxed text-[#5B4A3F] md:text-lg" data-testid="home-personalized-nutrition-paragraph-1">
              I’m Shyra, a health coach and weight management specialist. I help
              my clients reach their fitness goals, whether that is fat loss,
              body recomposition, or building lean muscle while keeping them
              energized, healthy, and feeling their best. My approach is fully
              personalized to your body, your lifestyle, and your food
              preferences, with balanced meal and supplement plans that are
              realistic and never restrictive.
            </p>

            <p className="mt-6 text-base leading-relaxed text-[#5B4A3F] md:text-lg" data-testid="home-personalized-nutrition-paragraph-2">
              No crash diets, no quick fixes. Just mindful habits and practical
              tools that help you make confident choices for life. You’ll see
              visible results now and set yourself up for better health in the
              future. Together, we build a strong body, calm mind, and a
              sustainable routine that works long after we’re done.
            </p>

            <p className="mt-5 font-heading text-2xl text-[#4A3B32]" data-testid="home-personalized-nutrition-signoff">
              – Shyra
            </p>
          </div>

          <div
            className="relative overflow-hidden rounded-[2rem] border border-[#E0D6C8] md:col-span-5"
            data-testid="home-personalized-nutrition-image-wrap"
          >
            <img
              src="/images/home-nutrition-board.webp"
              alt="Tilted wooden chopping board with handle on beige knit fabric, with berries, peach slices, pumpkin and chia seeds in soft shade light"
              className="h-full min-h-[440px] w-full object-cover object-center"
              data-testid="home-personalized-nutrition-image"
              loading="lazy"
              decoding="async"
            />
          </div>
        </motion.section>

        <motion.section
          className="rounded-[2rem] border border-[#E7DDCF] bg-[#FBF7F0] p-8 md:p-12"
          variants={sectionVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          data-testid="home-multifaceted-section"
        >
          <h2
            className="max-w-3xl font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl"
            data-testid="home-multifaceted-title"
          >
            Your health is multi-faceted. Your plan should be too.
          </h2>

          <div className="mt-8 grid grid-cols-1 gap-7 md:grid-cols-12" data-testid="home-multifaceted-content-grid">
            <p
              className="text-base leading-relaxed text-[#5B4A3F] md:col-span-7 md:text-lg"
              data-testid="home-multifaceted-description"
            >
              I combine nutrition, daily routines, accountability, and practical
              tools to transform your body and mind, now and in the long run.
            </p>

            <div
              className="rounded-2xl border border-[#E0D6C8] bg-white/70 p-5 md:col-span-5"
              data-testid="home-multifaceted-highlight-box"
            >
              <p className="font-heading text-2xl leading-tight text-[#4A3B32]" data-testid="home-multifaceted-highlight-text">
                Feel good. Look better. Live lighter.
              </p>
            </div>
          </div>
        </motion.section>

        <motion.section
          variants={sectionVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          data-testid="home-program-preview-section"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#7A6658]" data-testid="home-program-preview-eyebrow">
            The program
          </p>
          <h2 className="mt-3 font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl" data-testid="home-program-preview-title">
            Your body, your plan. Fully customized.
          </h2>

          <ul className="mt-8 space-y-3" data-testid="home-program-preview-list">
            {programHighlights.map((item, index) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-2xl border border-[#E0D6C8] bg-white/70 px-4 py-4 text-sm leading-relaxed text-[#4A3B32] md:text-base"
                data-testid={`home-program-preview-item-${index + 1}`}
              >
                <Leaf className="mt-0.5 h-4 w-4 shrink-0 text-[#8B7355]" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="pt-7" data-testid="home-program-preview-link-wrap">
            <Link
              to="/the-program"
              className="inline-flex items-center gap-2 text-base text-[#4A3B32] underline decoration-[#BFA88E] underline-offset-4 transition-colors hover:text-[#6B5A4E]"
              data-testid="home-program-preview-link"
            >
              Find out more here <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </motion.section>

        <motion.section
          className="scroll-mt-24 md:scroll-mt-28"
          variants={sectionVariants}
          initial="hidden"
          animate={isTestimonialsHash ? "show" : undefined}
          whileInView={isTestimonialsHash ? undefined : "show"}
          viewport={{ once: true, amount: 0.2 }}
          id="testimonials"
          data-testid="home-testimonials-section"
        >
          <h2 className="font-heading text-3xl leading-tight text-[#4A3B32] md:text-4xl" data-testid="home-testimonials-title">
            TESTIMONIALS
          </h2>

          <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="home-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.article
                key={`${testimonial.author}-${index}`}
                className={`rounded-[1.6rem] border border-[#E0D6C8] p-6 ${
                  index % 2 === 0 ? "bg-[#FAF5EC]" : "bg-[#F2E8DA]"
                } ${index === 2 ? "md:col-span-2" : ""}`}
                data-testid={`home-testimonial-card-${index + 1}`}
                initial={{ opacity: 0, y: 14 }}
                animate={isTestimonialsHash ? { opacity: 1, y: 0 } : undefined}
                whileInView={
                  isTestimonialsHash ? undefined : { opacity: 1, y: 0 }
                }
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: 0.45,
                  ease: [0.22, 1, 0.36, 1],
                  delay: index * 0.03,
                }}
              >
                <p className="text-sm leading-relaxed text-[#4F4036] md:text-base" data-testid={`home-testimonial-text-${index + 1}`}>
                  {testimonial.text}
                </p>
                <p className="mt-4 font-heading text-xl text-[#4A3B32]" data-testid={`home-testimonial-author-${index + 1}`}>
                  - {testimonial.author}
                </p>
              </motion.article>
            ))}
          </div>
        </motion.section>
      </main>

      <footer className="mx-6 mb-8 rounded-[2rem] bg-[#4A3B32] px-6 py-14 text-[#FDFBF7] md:mx-12 md:px-12" data-testid="home-contact-section">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-7">
          <h2 className="font-heading text-3xl leading-tight md:text-4xl" data-testid="home-contact-title">
            GET IN TOUCH
          </h2>

          <div className="flex items-center gap-4" data-testid="home-social-links-wrap">
            <a
              href="https://www.instagram.com/shyrabhalla?igsh=MW50ZmI1Z2tqMmt2bA%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#CDB9A0] bg-[#5A493E] transition-colors hover:bg-[#6B5A4E]"
              data-testid="home-instagram-link"
              aria-label="Instagram"
            >
              <Instagram className="h-5 w-5" aria-hidden="true" />
            </a>

            <a
              href="https://linkedin.com/in/shyra-bhalla-276907194"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#CDB9A0] bg-[#5A493E] transition-colors hover:bg-[#6B5A4E]"
              data-testid="home-linkedin-link"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-5 w-5" aria-hidden="true" />
            </a>
          </div>

          <div className="pt-1" data-testid="home-contact-cta-wrap">
            <Button
              asChild
              className="h-auto rounded-full bg-[#FDFBF7] px-8 py-4 text-base text-[#4A3B32] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#EFE6D8]"
            >
              <a
                href={TYPEFORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-lets-talk-button"
              >
                Let’s Talk <ArrowRight aria-hidden="true" />
              </a>
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
